import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Label } from '../../components/ui/Label'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import { PasswordStrengthIndicator } from '../../components/auth/PasswordStrengthIndicator'
import { authApi } from '../../services/api/auth'
import toast from 'react-hot-toast'

const resetPasswordSchema = z
  .object({
    newPassword: z.string().min(12, 'Password must be at least 12 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

interface ResetPasswordPageProps {
  getLocationHash?: () => string
}

export const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({
  getLocationHash = () => window.location.hash,
}) => {
  const navigate = useNavigate()
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [tokenError, setTokenError] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    const hash = getLocationHash()
    const params = new URLSearchParams(hash.replace(/^#/, ''))
    const type = params.get('type')
    const token = params.get('access_token')
    if (type === 'recovery' && token) {
      setAccessToken(token)
    } else {
      setTokenError(true)
    }
  }, [getLocationHash])

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  })

  const watchedPassword = watch('newPassword', '')

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!accessToken) return
    setSubmitError(null)
    try {
      await authApi.resetPassword(accessToken, data.newPassword)
      toast.success('Password updated. Please sign in.')
      navigate('/login', { replace: true })
    } catch (error: any) {
      setSubmitError(error?.message || 'Failed to reset password. Please request a new link.')
    }
  }

  if (tokenError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center space-y-4">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Invalid reset link
          </h1>
          <p className="text-sm text-muted-foreground">
            This link is invalid or has expired. Please request a new one.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/forgot-password')}
          >
            Request new link
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Set a new password
          </h1>
          <p className="text-sm text-muted-foreground">
            Choose a strong password with at least 12 characters.
          </p>
        </div>

        <div className="bg-card border border-border rounded-[28px] shadow-lg p-8 space-y-6">
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="space-y-5"
          >
            <div className="space-y-1.5">
              <Label htmlFor="new-password">New password</Label>
              <div className="relative">
                <Input
                  {...register('newPassword')}
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Enter new password"
                  className="pr-10"
                  aria-invalid={errors.newPassword ? 'true' : 'false'}
                  aria-describedby={errors.newPassword ? 'new-password-error' : undefined}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute inset-y-0 right-0 h-full w-10"
                  onClick={() => setShowPassword((prev) => !prev)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.newPassword && (
                <p
                  id="new-password-error"
                  role="alert"
                  className="text-sm text-destructive"
                >
                  {errors.newPassword.message}
                </p>
              )}
              <PasswordStrengthIndicator password={watchedPassword} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <div className="relative">
                <Input
                  {...register('confirmPassword')}
                  id="confirm-password"
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                  className="pr-10"
                  aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                  aria-describedby={errors.confirmPassword ? 'confirm-password-error' : undefined}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute inset-y-0 right-0 h-full w-10"
                  onClick={() => setShowConfirm((prev) => !prev)}
                  title={showConfirm ? 'Hide password' : 'Show password'}
                >
                  {showConfirm ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.confirmPassword && (
                <p
                  id="confirm-password-error"
                  role="alert"
                  className="text-sm text-destructive"
                >
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            {submitError && (
              <p role="alert" className="text-sm text-destructive">
                {submitError}
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting || !accessToken}
            >
              {isSubmitting ? (
                <LoadingSpinner
                  size="sm"
                  variant="icon"
                  className="mr-2"
                />
              ) : null}
              Update password
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}
